import { Check, RotateCcw, Send, Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { awardFeat, awardPoints } from "../utils/rewards";
import { Footer } from "../components/Footer";
import { PalsCloudSave, getLinkedTag, usePalsSync } from "../components/PalsCloudSave";
import { SectionHeading } from "../components/SectionHeading";
import { DolosComplianceBingo } from "../components/DolosComplianceBingo";
import { CritterBook, LoreVault, ShardShop, TitanExpeditions, TitanRadar, TrophyWall, isShinySunday, isTradeTuesday, tradeValue, trophyProgress, titanLevel, useExpeditions } from "../components/TitanExpeditions";
import { CritterBanner } from "../components/CritterBanner";
import { ArrowLeft, Flame, PackageOpen } from "lucide-react";
import { toast } from "../components/ui/sonner";
import { COMING_SOON, CRITTERS, DOLOPALS, RARITY, SHOP_ITEMS, seededRand } from "../data/dolopals";

const STORAGE_KEY = "dolopals-collection";

const loadCollection = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return Array.isArray(parsed) ? parsed.filter((id) => DOLOPALS.some((p) => p.id === id)) : [];
  } catch {
    return [];
  }
};

const sfx = (src, vol = 0.4) => {
  try {
    const a = new Audio(src);
    a.volume = vol;
    a.play().catch(() => {});
  } catch {}
};

// low rumbling rattle while the crate shakes, synthesized so no extra download is needed
const rumbleSfx = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const dur = 0.95;
    const buffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i += 1) {
      data[i] = (Math.random() * 2 - 1) * (0.25 + 0.75 * (i / data.length));
    }
    const srcNode = ctx.createBufferSource();
    srcNode.buffer = buffer;
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 170;
    const gain = ctx.createGain();
    gain.gain.value = 0.5;
    srcNode.connect(filter).connect(gain).connect(ctx.destination);
    srcNode.start();
    srcNode.onended = () => ctx.close();
  } catch {}
};

// weighted pull matching the legend: common 55%, rare 32%, legendary 13% — uniform within each rarity
// rarePlus (daily streak 3+) drops commons entirely: rare 71%, legendary 29%
const pullId = (rarePlus = false) => {
  const roll = Math.random();
  const rarity = rarePlus ? (roll < 0.71 ? "rare" : "legendary") : roll < 0.55 ? "common" : roll < 0.87 ? "rare" : "legendary";
  const pool = DOLOPALS.filter((p) => p.rarity === rarity);
  return pool[Math.floor(Math.random() * pool.length)].id;
};

// gold explosion overlay when a Legendary Wasteland Edition is pulled
function LegendaryBurst() {
  const [parts] = useState(() =>
    Array.from({ length: 30 }, (_, i) => {
      const angle = Math.random() * Math.PI * 2;
      const dist = 90 + Math.random() * 150;
      return {
        id: i,
        tx: Math.round(Math.cos(angle) * dist),
        ty: Math.round(Math.sin(angle) * dist - 70),
        rot: Math.floor(Math.random() * 540),
        dl: (Math.random() * 0.15).toFixed(2),
        big: i % 3 === 0,
      };
    })
  );
  return (
    <div className="unbox-burst" data-testid="unbox-burst">
      <span className="unbox-ring" />
      <span className="unbox-ring is-late" />
      {parts.map((p) => (
        <span
          key={p.id}
          className={`unbox-shard ${p.big ? "is-big" : ""}`}
          style={{ "--tx": `${p.tx}px`, "--ty": `${p.ty}px`, "--rot": `${p.rot}deg`, "--dl": `${p.dl}s` }}
        />
      ))}
    </div>
  );
}

// encrypted Series 02 teaser: a static-choked silhouette that sharpens with lore fragments
function SeriesSilhouette({ seed, clarity }) {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const W = canvas.width;
    const H = canvas.height;
    const ctx = canvas.getContext("2d");
    const rnd = seededRand(seed);
    const cx = W / 2;
    const bodyW = 70 + rnd() * 46;
    const bodyH = 78 + rnd() * 44;
    const bodyY = H * 0.64;
    const headR = 26 + rnd() * 14;
    const headX = cx + (rnd() - 0.5) * 22;
    const headY = bodyY - bodyH / 2 - headR * 0.55;
    const earLen = 14 + rnd() * 22;
    const legs = 2 + Math.floor(rnd() * 2);
    const tail = rnd() > 0.5;
    ctx.clearRect(0, 0, W, H);
    ctx.save();
    try {
      ctx.filter = `blur(${(14 * (1 - clarity)).toFixed(1)}px)`;
    } catch {}
    ctx.fillStyle = "#101010";
    if (tail) {
      ctx.beginPath();
      ctx.ellipse(cx + bodyW / 2 + 16, bodyY - 10, 22, 8, -0.5, 0, Math.PI * 2);
      ctx.fill();
    }
    for (let i = 0; i < legs; i += 1) {
      const lx = cx - bodyW / 2 + 12 + (i * (bodyW - 24)) / Math.max(1, legs - 1);
      ctx.fillRect(lx - 6, bodyY + bodyH / 2 - 8, 12, 30);
    }
    ctx.beginPath();
    ctx.ellipse(cx, bodyY, bodyW / 2, bodyH / 2, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(headX, headY, headR, 0, Math.PI * 2);
    ctx.fill();
    const earSpread = headR * 0.55;
    [-1, 1].forEach((side) => {
      ctx.beginPath();
      ctx.moveTo(headX + side * earSpread, headY - headR * 0.6);
      ctx.lineTo(headX + side * (earSpread + 6), headY - headR * 0.6 - earLen);
      ctx.lineTo(headX + side * (earSpread - 8), headY - headR * 0.35);
      ctx.closePath();
      ctx.fill();
    });
    ctx.restore();
    // amber eyes burn through once the signal is half clean
    const eyeA = Math.max(0, (clarity - 0.4) * 1.7);
    if (eyeA > 0.02) {
      ctx.save();
      ctx.globalAlpha = Math.min(1, eyeA);
      ctx.fillStyle = "#ff9900";
      ctx.shadowColor = "#ff9900";
      ctx.shadowBlur = 12;
      ctx.beginPath();
      ctx.arc(headX - headR * 0.38, headY - 2, 3.4, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(headX + headR * 0.38, headY - 2, 3.4, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
    // broadcast static, thinning as clarity rises
    const n = Math.floor(340 * (1 - clarity));
    for (let i = 0; i < n; i += 1) {
      const nr = seededRand(`${seed}-n${i}`);
      ctx.fillStyle = nr() > 0.85 ? "rgba(255, 153, 0, 0.1)" : "rgba(255, 255, 255, 0.06)";
      ctx.fillRect(nr() * W, nr() * H, 2, 2);
    }
    ctx.fillStyle = "rgba(0, 0, 0, 0.25)";
    for (let y = 0; y < H; y += 4) ctx.fillRect(0, y, W, 1);
  }, [seed, clarity]);
  return <canvas ref={ref} className="soon-canvas" width={220} height={260} data-testid={`soon-canvas-${seed}`} />;
}

export default function DoloPals() {
  const [collection, setCollection] = useState(loadCollection);
  const [boxStage, setBoxStage] = useState("idle"); // idle | fall | shake | open
  const [revealed, setRevealed] = useState(null); // { pal, isNew, key }
  const timers = useRef([]);
  const location = useLocation();
  const { exp, now, send, claim, buy, equip, setExp } = useExpeditions();
  const [searchParams] = useSearchParams();
  // deep link from the homepage banner: land straight in the match game
  const startInMatch = searchParams.get("play") === "match";
  useEffect(() => {
    if (!startInMatch) return undefined;
    const t = window.setTimeout(() => {
      document.querySelector('[data-testid="critter-book"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 700);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // radar mini-game payouts: signal taps and match rounds pay scrap-shards
  const awardShards = (n) => setExp((e) => ({ ...e, shards: e.shards + n }));
  // scrap critters: adopt new ones; repeats become trade stock — shiny gold variants pay double
  const collectCritter = (id, shiny = false) => {
    const critter = CRITTERS.find((c) => c.id === id);
    const hasCritter = (exp.critters || []).includes(id);
    const hasShiny = (exp.shinies || []).includes(id);
    setExp((e) => {
      const next = { ...e };
      if (!hasCritter) next.critters = [...(e.critters || []), id];
      if (shiny) {
        if (!hasShiny) next.shinies = [...(e.shinies || []), id];
        else next.shinyDupes = { ...(e.shinyDupes || {}), [id]: ((e.shinyDupes || {})[id] || 0) + 1 };
        if (isShinySunday()) next.sundayShinies = (e.sundayShinies || 0) + 1;
      } else if (hasCritter) {
        next.critterDupes = { ...(e.critterDupes || {}), [id]: ((e.critterDupes || {})[id] || 0) + 1 };
      }
      return next;
    });
    if (shiny && !hasShiny) toast.success(`A SHINY ${critter ? critter.name : "CRITTER"}! IT GLITTERS IN YOUR BOOK.`);
    else if (shiny) toast.info(`SHINY ${critter ? critter.name : "CRITTER"} DUPLICATE — TRADES FOR DOUBLE.`);
    else if (!hasCritter) toast.success(`YOU FOUND A ${critter ? critter.name : "CRITTER"}! (${(exp.critters || []).length + 1}/${CRITTERS.length})`);
    else toast.info(`ANOTHER ${critter ? critter.name : "CRITTER"} — TRADE IT IN THE SHARD SHOP FOR +15.`);
  };
  // trade one duplicate critter (shiny dupes pay double; Trade Tuesday doubles everything)
  const tradeCritter = (id, shiny = false) => {
    const critter = CRITTERS.find((c) => c.id === id);
    const stock = shiny ? exp.shinyDupes || {} : exp.critterDupes || {};
    if ((stock[id] || 0) <= 0) return;
    const pay = tradeValue() * (shiny ? 2 : 1);
    setExp((e) => {
      const pool = { ...((shiny ? e.shinyDupes : e.critterDupes) || {}) };
      pool[id] = (pool[id] || 0) - 1;
      if (pool[id] <= 0) delete pool[id];
      return shiny ? { ...e, shinyDupes: pool, shards: e.shards + pay } : { ...e, critterDupes: pool, shards: e.shards + pay };
    });
    toast.success(`TRADED A ${shiny ? "SHINY " : ""}${critter ? critter.name : "CRITTER"} — +${pay} SCRAP-SHARDS${isTradeTuesday() ? " (TRADE TUESDAY DOUBLE)" : ""}.`);
  };
  // persistent Critter Match record — feeds the MATCH MASTER trophy on the wall
  const saveMatchBest = (round) => setExp((e) => ({ ...e, matchBest: Math.max(e.matchBest || 0, round) }));
  // worm contacts jam a runner's expedition clock
  const slowRun = (titanId, ms) =>
    setExp((e) => {
      const run = e.active[titanId];
      if (!run) return e;
      return { ...e, active: { ...e.active, [titanId]: { ...run, endsAt: run.endsAt + ms } } };
    });
  // lifetime signal catches -> Titan Tag badges at 10 / 50 / 100 (backend dedupes repeats)
  const CATCH_MILESTONES = [10, 50, 100];
  const caughtRef = useRef(0);
  useEffect(() => {
    caughtRef.current = exp.caught || 0;
  }, [exp.caught]);
  const bumpCaught = () => {
    caughtRef.current += 1;
    const total = caughtRef.current;
    setExp((e) => ({ ...e, caught: Math.max(e.caught || 0, total) }));
    if (CATCH_MILESTONES.includes(total)) awardFeat(`SIGNAL CATCHER ×${total}`);
  };
  // mega worm kills -> Titan Tag badges at 1 / 10 / 25 (backend dedupes repeats)
  const MEGA_MILESTONES = [1, 10, 25];
  const megaRef = useRef(0);
  useEffect(() => {
    megaRef.current = exp.megaKills || 0;
  }, [exp.megaKills]);
  const bumpMegaKills = () => {
    megaRef.current += 1;
    const total = megaRef.current;
    setExp((e) => ({ ...e, megaKills: Math.max(e.megaKills || 0, total) }));
    if (MEGA_MILESTONES.includes(total)) awardFeat(total === 1 ? "MEGA WORM SLAYER" : `MEGA WORM SLAYER ×${total}`);
  };
  // defense runs post their best score to the league leaderboard under your linked tag
  const saveDefense = async (score) => {
    const best = Math.max(exp.defenseBest || 0, score);
    const record = score > (exp.defenseBest || 0);
    setExp((e) => ({ ...e, defenseBest: Math.max(e.defenseBest || 0, score) }));
    let leader = null;
    const tag = getLinkedTag();
    if (tag) {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rewards/defense`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tag, score }),
        });
        const data = await res.json();
        leader = data.leader || null;
      } catch {}
    }
    if (record && score > 0) toast.success(`NEW DEFENSE RECORD — ${score} FANGS DOWN.`);
    return { best, record, leader };
  };
  // treasure ping: one buried cache a day, +50 shard jackpot
  const claimTreasure = () => {
    setExp((e) => ({ ...e, treasure: dayKey(new Date()), shards: e.shards + 50 }));
    toast.success("TREASURE CACHE CRACKED — +50 SCRAP-SHARDS.");
  };
  const saveOutpost = (name) => {
    setExp((e) => ({ ...e, outpost: name }));
    toast.success(`OUTPOST RENAMED — ${name} ON THE AIR.`);
  };
  // ---------- while-you-were-gone report ----------
  const VISIT_KEY = "dolopals-last-visit";
  const [goneReport, setGoneReport] = useState(null);
  useEffect(() => {
    const prev = Number(localStorage.getItem(VISIT_KEY)) || 0;
    const awayMs = Date.now() - prev;
    if (prev && awayMs > 30 * 60 * 1000) {
      const runs = Object.values(exp.active || {});
      const ready = runs.filter((r) => r.endsAt <= Date.now()).length;
      const afield = runs.length - ready;
      const streakAlive = exp.daily?.streak > 0;
      const claimedToday = exp.daily?.last === dayKey(new Date());
      setGoneReport({
        awayH: awayMs / 3600000,
        ready,
        afield,
        streak: exp.daily?.streak || 0,
        streakAlive,
        claimedToday,
      });
    }
    localStorage.setItem(VISIT_KEY, String(Date.now()));
    // mount-time snapshot only — the report describes the state we returned to
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const targetId = location.hash === "#dolo-bingo"
      ? "dolo-bingo"
      : location.hash === "#adopt-a-tiny-titan"
        ? "adopt-a-tiny-titan"
        : null;
    if (!targetId) return undefined;
    const timer = window.setTimeout(() => {
      document.getElementById(targetId)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [location.hash]);

  // trophy toasts — a gold flash the moment any wall milestone flips earned mid-session
  const earnedRef = useRef(null);
  useEffect(() => {
    const list = trophyProgress(exp, collection);
    const ids = list.filter((t) => t.got).map((t) => t.id);
    if (earnedRef.current === null) {
      earnedRef.current = ids;
      return;
    }
    list
      .filter((t) => t.got && !earnedRef.current.includes(t.id))
      .forEach((t) => {
        toast(`TROPHY UNLOCKED — ${t.name}`, { description: t.need, duration: 5000, className: "trophy-toast" });
      });
    earnedRef.current = ids;
  }, [exp, collection]);
  // ---------- daily drop crate (hard-reset streak, rare-or-better from day 3) ----------
  const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const daily = exp.daily || { last: null, streak: 0 };
  const claimedToday = daily.last === dayKey(new Date());
  const nextMidnight = new Date();
  nextMidnight.setHours(24, 0, 0, 0);
  const dropSecs = Math.max(0, Math.floor((nextMidnight.getTime() - now) / 1000));
  const dropCountdown = `${Math.floor(dropSecs / 3600)}H ${String(Math.floor(dropSecs / 60) % 60).padStart(2, "0")}M`;
  const claimDailyDrop = () => {
    if (claimedToday || boxStage === "fall" || boxStage === "shake") return;
    const yKey = dayKey(new Date(Date.now() - 86400000));
    const streak = daily.last === yKey ? daily.streak + 1 : 1; // miss a day -> hard reset
    setExp((e) => ({ ...e, daily: { last: dayKey(new Date()), streak } }));
    toast.success(`DAILY DROP CLAIMED — STREAK ${streak} DAY${streak > 1 ? "S" : ""}${streak >= 3 ? " — RARE-OR-BETTER LUCK ACTIVE" : ""}.`);
    unbox(streak >= 3);
  };
  const sync = usePalsSync({ collection, setCollection, exp, setExp });
  const activeFrame = SHOP_ITEMS.find((i) => i.id === exp.frame);
  const activeBanner = SHOP_ITEMS.find((i) => i.id === exp.banner);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(collection));
  }, [collection]);

  useEffect(() => () => timers.current.forEach((t) => window.clearTimeout(t)), []);

  const adopt = (id) => setCollection((c) => (c.includes(id) ? c : [...c, id]));
  const unadopt = (id) => setCollection((c) => c.filter((x) => x !== id));

  const unbox = (rarePlus = false) => {
    if (boxStage === "fall" || boxStage === "shake") return;
    setRevealed(null);
    setBoxStage("fall");
    sfx("/assets/game-coin.wav", 0.25);
    timers.current.push(window.setTimeout(() => {
      setBoxStage("shake");
      rumbleSfx();
    }, 780));
    timers.current.push(window.setTimeout(() => {
      setBoxStage("open");
      sfx("/assets/game-blast.wav", 0.3);
    }, 1730));
    timers.current.push(window.setTimeout(() => {
      const pal = DOLOPALS.find((p) => p.id === pullId(rarePlus));
      if (!pal) return;
      const isNew = !collection.includes(pal.id);
      setRevealed({ pal, isNew, key: Date.now() });
      if (isNew) adopt(pal.id);
      awardPoints("dolopals", pal.rarity === "legendary" ? 5 : 1);
      if (pal.rarity === "legendary") awardFeat("LEGENDARY PULL");
      sfx(pal.rarity === "legendary" ? "/assets/game-blast.wav" : "/assets/game-coin.wav", 0.5);
    }, 2050));
  };

  const owned = collection.length;
  const total = DOLOPALS.length;

  return (
    <main className="dolopals-page" data-testid="dolopals-page">
      <DolosComplianceBingo />

      <section id="adopt-a-tiny-titan" className="dolopals-hero">
        <Link to="/" className="back-link" data-testid="dolopals-back-link">
          <ArrowLeft size={16} /> RETURN TO WORLD
        </Link>
        <img
          className={`dolopals-banner ${activeBanner ? `banner-${activeBanner.id}` : ""}`}
          src="/assets/pals/dolopals-banner.jpg"
          alt="DoloPals Tiny Titans — Collect. Battle. Survive. Cutely."
          data-testid="dolopals-banner"
          fetchPriority="high"
          decoding="async"
        />
        <p className="eyebrow dolopals-eyebrow">KIDZONE INTERACTIVE // DIGITAL DOLOPAL REGISTRY</p>
        <h1>ADOPT A TINY TITAN.</h1>
        <p className="dolopals-hero-copy">
          The wasteland is big, scary, and honestly a little lonely — that's why the Tiny Titans exist. Unbox digital
          drops, meet the crew, and keep your adoption checklist. Legendary Wasteland Editions are out there. Somewhere.
        </p>
        <div className="dolopals-progress" data-testid="dolopals-progress">
          <div className="dolopals-progress-bar">
            <span style={{ width: `${(owned / total) * 100}%` }} />
          </div>
          <strong data-testid="dolopals-progress-count">{owned} / {total} TITANS ADOPTED</strong>
        </div>
      </section>

      <PalsCloudSave {...sync} collection={collection} exp={exp} />

      <CritterBanner compact />

      {goneReport ? (
        <section className="gone-report" data-testid="gone-report">
          <div className="gone-report-head">
            <span className="eyebrow">TRANSMISSION // WHILE YOU WERE GONE</span>
            <button type="button" onClick={() => setGoneReport(null)} data-testid="gone-report-close" aria-label="Dismiss report">×</button>
          </div>
          <ul>
            <li>{goneReport.awayH >= 1 ? `${Math.round(goneReport.awayH)}H OFF THE GRID — WELCOME BACK, KID` : "BACK BEFORE THE STATIC EVEN SETTLED"}</li>
            <li>{goneReport.ready > 0 ? `${goneReport.ready} MISSION${goneReport.ready > 1 ? "S" : ""} FINISHED — LOOT WAITING IN EXPEDITIONS` : "NO MISSIONS FINISHED — SEND A RUNNER OUT BEFORE YOU LEAVE"}</li>
            <li>{goneReport.afield > 0 ? `${goneReport.afield} TITAN${goneReport.afield > 1 ? "S" : ""} STILL AFIELD ON THE CLOCK` : "ALL TITANS HOME AND IDLE"}</li>
            <li>
              {goneReport.streakAlive
                ? goneReport.claimedToday
                  ? `DAILY DROP STREAK SAFE — DAY ${goneReport.streak} LOCKED IN`
                  : `DAILY DROP STREAK AT RISK — DAY ${goneReport.streak} DIES AT MIDNIGHT, CLAIM NOW`
                : "NO DAILY STREAK YET — CLAIM TODAY'S DROP TO LIGHT ONE"}
            </li>
          </ul>
        </section>
      ) : null}

      <section className="dolopals-unbox" data-testid="dolopals-unbox">
        <SectionHeading
          eyebrow="DROP SIMULATOR // SERIES 01"
          title="Unbox a titan drop."
          text="Every digital pack holds one Tiny Titan. Commons stroll out, Rares strut, and the Legendary Wasteland Edition practically explodes. Duplicates become trade bait."
        />
        <div className="daily-drop" data-testid="daily-drop-panel">
          <div className="daily-drop-info">
            <span className="daily-drop-tag"><Flame size={13} /> DAILY DROP</span>
            <strong data-testid="daily-drop-streak">STREAK — {daily.streak} DAY{daily.streak === 1 ? "" : "S"}</strong>
            <p>One free crate every day. Hold a 3-day streak and the drop runs RARE-OR-BETTER. Miss a day and the streak resets to zero.</p>
          </div>
          <button
            type="button"
            className={`daily-drop-button ${claimedToday ? "is-claimed" : ""}`}
            onClick={claimDailyDrop}
            disabled={claimedToday || boxStage === "fall" || boxStage === "shake"}
            data-testid="daily-drop-button"
          >
            <PackageOpen size={15} /> {claimedToday ? `NEXT DROP IN ${dropCountdown}` : "CLAIM DAILY DROP"}
          </button>
        </div>
        <div className="unbox-stage">
          <div className={`unbox-capsule stage-${boxStage}`} data-testid="unbox-capsule">
            {revealed && revealed.pal.rarity === "legendary" ? <LegendaryBurst key={revealed.key} /> : null}
            <div className="unbox-crate">
              {revealed ? (
                <div className="unbox-reveal" key={revealed.key} style={{ "--rc": RARITY[revealed.pal.rarity].color }} data-testid="unbox-reveal">
                  <img src={revealed.pal.art} alt={`${revealed.pal.name} unboxed`} />
                  <strong>{revealed.pal.name}</strong>
                  <span className="unbox-rarity">{RARITY[revealed.pal.rarity].label}</span>
                  <em data-testid="unbox-verdict">{revealed.isNew ? "NEW ADOPTION — ADDED TO YOUR CHECKLIST" : "DUPLICATE — TRADE BAIT FOR THE SCHOOLYARD"}</em>
                </div>
              ) : null}
              <img
                className="unbox-crate-img"
                src={boxStage === "open" ? "/assets/pals/crate-open.png" : "/assets/pals/crate-closed.png"}
                alt="DoloPals titan drop crate"
                data-testid="unbox-crate-img"
              />
            </div>
          </div>
          <button type="button" className="unbox-button" onClick={unbox} disabled={boxStage === "fall" || boxStage === "shake"} data-testid="unbox-button">
            <Send size={15} /> {boxStage === "fall" ? "DROP INBOUND…" : boxStage === "shake" ? "SOMETHING'S RATTLING…" : "UNBOX A TITAN DROP"}
          </button>
        </div>
        <div className="rarity-legend" data-testid="rarity-legend">
          {Object.values(RARITY).map((r) => (
            <span key={r.label} className="rarity-chip" style={{ "--rc": r.color }}>
              {r.label} · {r.odds}
            </span>
          ))}
        </div>
      </section>

      <TitanExpeditions exp={exp} now={now} send={send} claim={claim} collection={collection} />

      <TitanRadar exp={exp} collection={collection} awardShards={awardShards} slowRun={slowRun} bumpCaught={bumpCaught} saveDefense={saveDefense} claimTreasure={claimTreasure} collectCritter={collectCritter} saveOutpost={saveOutpost} bumpMegaKills={bumpMegaKills} />

      <CritterBook exp={exp} awardShards={awardShards} awardPoints={awardPoints} collectCritter={collectCritter} saveMatchBest={saveMatchBest} startInMatch={startInMatch} />

      <TrophyWall exp={exp} collection={collection} />

      <ShardShop exp={exp} buy={buy} equip={equip} tradeCritter={tradeCritter} />

      <LoreVault fragments={exp.fragments} />

      <section className="dolopals-roster" data-testid="dolopals-roster">
        <SectionHeading
          eyebrow="THE REGISTRY // MEET THE CREW"
          title="Tiny titans, huge hearts."
          text="Tap MARK ADOPTED when the real toy joins your shelf — your checklist saves on this device."
        />
        <div className={`pal-grid ${activeFrame ? "has-frame" : ""}`} style={activeFrame ? { "--frame": activeFrame.color } : undefined}>
          {DOLOPALS.map((pal) => {
            const isOwned = collection.includes(pal.id);
            const rarity = RARITY[pal.rarity];
            return (
              <article key={pal.id} className={`pal-card ${isOwned ? "is-owned" : ""}`} style={{ "--rc": rarity.color }} data-testid={`pal-card-${pal.id}`}>
                <div className="pal-art">
                  <img src={pal.art} alt={`${pal.name} — ${pal.title}`} loading="lazy" decoding="async" />
                  {isOwned ? <span className="pal-stamp" data-testid={`pal-stamp-${pal.id}`}><Check size={12} /> ADOPTED</span> : null}
                  <span className="pal-rarity">{rarity.label}</span>
                </div>
                <div className="pal-body">
                  <h3>{pal.name} <span className="pal-level" data-testid={`pal-level-${pal.id}`}>LVL {titanLevel(exp.xp[pal.id])}</span></h3>
                  <em>{pal.title}</em>
                  <p>{pal.bio}</p>
                  <div className="pal-stats" data-testid={`pal-stats-${pal.id}`}>
                    {Object.entries(pal.stats).map(([k, v]) => (
                      <div key={k} className="pal-stat">
                        <span>{k}</span>
                        <div className="pal-stat-bar"><i style={{ width: `${v}%` }} /></div>
                        <b>{v}</b>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    className={`pal-adopt ${isOwned ? "is-owned" : ""}`}
                    onClick={() => (isOwned ? unadopt(pal.id) : adopt(pal.id))}
                    data-testid={`pal-adopt-${pal.id}`}
                  >
                    {isOwned ? <><Check size={13} /> ON YOUR SHELF</> : "MARK ADOPTED"}
                  </button>
                </div>
              </article>
            );
          })}
          {COMING_SOON.map((slot, idx) => {
            const clarity = Math.max(0, Math.min(1, exp.fragments / (10 + idx * 4)));
            return (
              <article key={slot.id} className="pal-card pal-card-soon" data-testid={slot.id}>
                <div className="pal-art pal-art-soon">
                  <SeriesSilhouette seed={slot.id} clarity={clarity} />
                </div>
                <div className="pal-body">
                  <h3>???</h3>
                  <em>{slot.label}</em>
                  <p>A new Tiny Titan signal is scrambling through the static. Keep your checklist ready — Series 02 drops when the tower stops humming.</p>
                  <p className="soon-progress" data-testid={`soon-progress-${slot.id}`}>
                    SIGNAL CLARITY {Math.round(clarity * 100)}% — LORE FRAGMENTS SHARPEN THE SILHOUETTE
                  </p>
                </div>
              </article>
            );
          })}
        </div>
        {owned === total ? (
          <p className="dolopals-complete" data-testid="dolopals-complete">
            <Sparkles size={14} /> FULL CREW ADOPTED — THE WASTELAND IS OFFICIALLY CUTER. SERIES 02 INCOMING.
          </p>
        ) : null}
        <button type="button" className="dolopals-reset" onClick={() => setCollection([])} data-testid="dolopals-reset">
          <RotateCcw size={12} /> RESET CHECKLIST
        </button>
      </section>
      <Footer />
    </main>
  );
}
